import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Save, Package, GripVertical } from 'lucide-react';

const PackingGroupsEditor = ({ recipe, isAdmin, onUpdate }) => {
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  // Get all unique component names across all size variations
  const allComponentNames = [...new Set(
    (recipe?.size_variations || []).flatMap(sv =>
      (sv.components || []).map(c => c.component_name)
    )
  )];

  useEffect(() => {
    setGroups(recipe?.packing_groups || []);
  }, [recipe?.id, recipe?.packing_groups]);

  const addGroup = () => {
    setGroups(prev => [...prev, {
      group_name: `Group ${prev.length + 1}`,
      packaging: '',
      packaging_notes: '',
      component_names: []
    }]);
  };

  const removeGroup = (idx) => {
    setGroups(prev => prev.filter((_, i) => i !== idx));
  };

  const updateGroup = (idx, field, value) => {
    setGroups(prev => prev.map((g, i) => i === idx ? { ...g, [field]: value } : g));
  };

  const toggleComponent = (groupIdx, compName) => {
    setGroups(prev => prev.map((g, i) => {
      if (i !== groupIdx) return g;
      const names = g.component_names.includes(compName)
        ? g.component_names.filter(n => n !== compName)
        : [...g.component_names, compName];
      return { ...g, component_names: names };
    }));
  };

  // Which components are already assigned to a group
  const assignedComponents = new Set(groups.flatMap(g => g.component_names));

  // Components not assigned to any group
  const unassignedComponents = allComponentNames.filter(n => !assignedComponents.has(n));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/recipes/${recipe.id}/packing-groups`, {
        packing_groups: groups
      });
      toast.success('Packing groups saved');
      if (onUpdate) onUpdate(res.data);
    } catch (e) {
      toast.error('Failed to save packing groups');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Packing Groups</h3>
        {groups.length === 0 ? (
          <p className="text-sm text-gray-400">No packing groups defined. Components will appear individually in sharing packs.</p>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => (
              <div key={g.group_name} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm">{g.group_name}</span>
                  <span className="text-xs text-gray-500">{g.packaging}{g.packaging_notes ? ` (${g.packaging_notes})` : ''}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {g.component_names.map(cn => (
                    <span key={cn} className="px-2 py-0.5 bg-white border border-gray-200 text-xs rounded">{cn}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="packing-groups-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Packing Groups</h3>
          <p className="text-xs text-gray-400 mt-1">Group components that should be packed together for Sharing clients</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={addGroup} data-testid="add-packing-group-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Group
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-packing-groups-btn">
            <Save className="w-3 h-3 mr-1" /> Save
          </Button>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-8 text-gray-400 border border-dashed border-gray-200 rounded-lg">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No packing groups defined</p>
          <p className="text-xs mt-1">Components will appear individually in sharing packs</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group, gIdx) => (
            <div key={gIdx} className="border border-gray-200 rounded-lg p-4 bg-gray-50" data-testid={`packing-group-${gIdx}`}>
              <div className="flex items-start gap-3 mb-3">
                <GripVertical className="w-4 h-4 text-gray-300 mt-2 shrink-0" />
                <div className="flex-1 grid grid-cols-3 gap-2">
                  <Input
                    value={group.group_name}
                    onChange={(e) => updateGroup(gIdx, 'group_name', e.target.value)}
                    placeholder="Group name"
                    className="text-sm h-9"
                    data-testid={`group-name-${gIdx}`}
                  />
                  <Input
                    value={group.packaging}
                    onChange={(e) => updateGroup(gIdx, 'packaging', e.target.value)}
                    placeholder="Packaging (e.g. Tray + Film)"
                    className="text-sm h-9"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={group.packaging_notes}
                      onChange={(e) => updateGroup(gIdx, 'packaging_notes', e.target.value)}
                      placeholder="Notes"
                      className="text-sm h-9 flex-1"
                    />
                    <button onClick={() => removeGroup(gIdx)} className="p-1.5 text-red-500 hover:bg-red-50 rounded shrink-0" data-testid={`remove-group-${gIdx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="ml-7">
                <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Components in this group:</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {allComponentNames.map(cn => {
                    const inThisGroup = group.component_names.includes(cn);
                    const inOtherGroup = !inThisGroup && assignedComponents.has(cn);
                    return (
                      <label
                        key={cn}
                        className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs cursor-pointer transition-all ${
                          inThisGroup
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                            : inOtherGroup
                              ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <Checkbox
                          checked={inThisGroup}
                          disabled={inOtherGroup}
                          onCheckedChange={() => toggleComponent(gIdx, cn)}
                          className="scale-75"
                        />
                        {cn}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unassigned components */}
      {unassignedComponents.length > 0 && groups.length > 0 && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Ungrouped components</span>
          <p className="text-xs text-amber-600 mt-1">These will appear as individual lines in sharing packs:</p>
          <div className="flex flex-wrap gap-1 mt-2">
            {unassignedComponents.map(cn => (
              <span key={cn} className="px-2 py-0.5 bg-white border border-amber-200 text-xs rounded text-amber-800">{cn}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PackingGroupsEditor;
